import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";

//import Components

//import actions
import { changeLayoutMode } from "../slices/thunks";

import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import EastIcon from "@mui/icons-material/East";
import { Typography, useMediaQuery } from "@mui/material";
import { withTranslation } from "react-i18next";

import logo from "assets/images/logo.svg";

import Header from "./Header";
import { getSidebarList } from "Layouts/VerticalLayouts/sidebarMenuList";
import VerticalLayoutMenu from "./VerticalLayouts";

const drawerWidth = 280;
const color = "#96a7c7";
const openedMixin = (theme) => ({
  width: drawerWidth,
  background: theme.palette.drawer.main,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  background: theme.palette.drawer.main,
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  background: "red",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const Layout = ({ children, t }) => {
  const isTablet = useMediaQuery("(max-width:1024px)");
  const isMobile = useMediaQuery("(max-width:768px)");

  const theme = useTheme();
  const [open, setOpen] = useState(!isTablet);

  useEffect(() => {
    if (isTablet) {
      setOpen(false);
    }
  }, [isTablet]);

  const handleDrawerOpen = () => {
    setOpen(!open);
  };

  const dispatch = useDispatch();
  const layoutModeType = useSelector((state) => state.Layout.layoutModeType);
  const roles = useSelector((state) => state.Login.roles);

  const onChangeLayoutMode = (value) => {
    if (changeLayoutMode) {
      dispatch(changeLayoutMode(value));
    }
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          open={open}
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Toolbar style={{ padding: "0px" }}>
            {!open && (
              <div style={{ width: "80px", height: "35px" }}>
                <img src={logo} style={{ width: "100%", height: "100%" }} />
              </div>
            )}

            <IconButton
              color="inherit"
              aria-label="open drawer"
              className="ms-1 p-2"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                zIndex: 99,
                paddingLeft: "15px",
              }}
            >
              {open ? <MenuIcon /> : <EastIcon />}
            </IconButton>
          </Toolbar>
          <Header
            layoutModeType={layoutModeType}
            onChangeLayoutMode={onChangeLayoutMode}
          />
        </AppBar>
        <Drawer
          variant="permanent"
          open={open}
          sx={{ display: open ? "block" : isMobile ? "none" : "block" }}
        >
          <DrawerHeader
            sx={{
              background: theme.palette.drawer.main,
              justifyContent: "center",
            }}
          >
            {open && (
              <div style={{ width: "80px", height: "35px" }}>
                <img src={logo} style={{ width: "100%", height: "100%" }} />
              </div>
            )}
          </DrawerHeader>
          {open && (
            <Typography sx={{ color: color, padding: "0 16px" }}>
              {t("Menu")}
            </Typography>
          )}
          <VerticalLayoutMenu
            menuItem={getSidebarList(roles)}
            isDrawerOpen={open}
          />
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: "calc(100% - 280px)",
            height: "100vh",
            padding: "16px",
          }}
        >
          <DrawerHeader />
          <div style={{ height: "calc(100% - 65px)" }}>{children}</div>
        </Box>
      </Box>
    </>
  );
};

Layout.propTypes = {
  children: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(Layout);
